import {
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import {
  Cancel,
  CheckCircle,
  Close,
  ExpandLess,
  ExpandMore,
  Replay,
  Report,
} from '@material-ui/icons'
import React, { useEffect, useMemo, useState } from 'react'
import ConfirmationDialog from 'core/components/ConfirmationDialog'
import { useUploadProgress } from 'core/context/uploadFileProgressContext'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { createImage, deleteImage, updateImage } from './actions'

const useStyles = makeStyles((theme) => ({
  card: {
    position: 'fixed',
    bottom: theme.spacing(3),
    right: theme.spacing(3),
    width: 350,
    backgroundColor: '#fff',
    borderRadius: 12,
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden',
    zIndex: 10,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    padding: theme.spacing(1, 2.5),
  },
  fileList: {
    maxHeight: 300,
    overflowY: 'auto',
    padding: theme.spacing(1),
    paddingBottom: theme.spacing(2),
  },
  fileItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1),
    '&:not(:last-child)': {
      borderBottom: '1px solid #ddd',
    },
  },
  fileInfo: {
    width: '75%',
  },
  fileName: {
    flexGrow: 1,
    fontSize: '0.9rem',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    marginBottom: theme.spacing(0.5),
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
  },
  successIcon: {
    color: theme.palette.success.main,
    cursor: 'pointer',
  },
  progressContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  progressText: {
    marginLeft: theme.spacing(1),
    fontSize: '0.85rem',
    color: '#666',
  },
}))

const FileUploadProgress = () => {
  const classes = useStyles()
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false)
  const [imageIdToCancel, setImageIdToCancel] = useState(null)
  const [abortFileUpload, setAbortFileUpload] = useState(null)
  const [deleteImageError, setDeleteImageError] = useState(null)
  const [isProtected, setIsProtected] = useState(false)
  const [isExpanded, setIsExpanded] = useState(true)

  const { files, resetFileStatus } = useUploadProgress()
  const { update: deleteFn, updating: deleting, error } = useUpdateAction(deleteImage)
  const { update: create, updating: creating } = useUpdateAction(createImage)
  const { update: updateImageDetails, updating: updating } = useUpdateAction(updateImage)

  const allUploadsComplete = useMemo(
    () => files?.every((file) => file?.progress === 100 || file?.error),
    [files],
  )
  const isUploading = useMemo(
    () => files?.some((file) => file?.progress < 100 && !file?.error),
    [files],
  )

  const toggleExpand = () => setIsExpanded(!isExpanded)

  const formatFileName = (name: string, maxLength: number = 40) => {
    if (name?.length <= maxLength) return name
    const start = name?.slice(0, maxLength / 2)
    const end = name?.slice(-(maxLength / 2))
    return `${start}...${end}`
  }

  const deleteFile = async (imageId, isProtected) => {
    if (isProtected) {
      const { success, error } = await updateImageDetails({
        id: imageId,
        body: [
          {
            op: 'replace',
            path: '/protected',
            value: false,
          },
        ],
      })
      if (!success) {
        setDeleteImageError(error || 'Failed to unprotect image')
        return false
      }
    }
    const { success: deleteSuccess } = await deleteFn({ id: imageId })
    return deleteSuccess
  }

  const closeFileProgress = (imageId) => {
    resetFileStatus(imageId)
  }

  const handleConfirm = async () => {
    abortFileUpload?.abort()
    const deleteSuccess = await deleteFile(imageIdToCancel, isProtected)
    if (deleteSuccess) {
      closeFileProgress(imageIdToCancel)
      setShowConfirmationDialog(false)
    }
  }

  const handleCancel = (imageId, reset, payload) => {
    setDeleteImageError(null)
    setShowConfirmationDialog(true)
    setIsProtected(payload?.protected || false)
    setImageIdToCancel(imageId)
    setAbortFileUpload(reset)
  }

  const closeCard = () => {
    if (allUploadsComplete) {
      files?.map((f) => resetFileStatus(f.id))
    }
  }

  const handleRetry = async (imageId: string, payload: any, retry: (imageId: string) => void) => {
    if (!imageId || creating || deleting) return
    try {
      const deleteSuccess = await deleteFile(imageId, payload?.protected)
      if (deleteSuccess) {
        const { response } = await create({ body: payload })
        closeFileProgress(imageId)
        if (response?.id) retry(response.id)
      }
    } catch (error) {
      console.error('Error retrying image upload:', error)
    }
  }

  useEffect(() => {
    if (error) {
      setDeleteImageError(error)
    }
  }, [error])

  return files.length > 0 ? (
    <>
      <Card className={classes.card}>
        <div className={classes.header}>
          <Typography variant="body1">Image Uploads ({files.length})</Typography>
          <div>
            <IconButton onClick={toggleExpand}>
              {isExpanded ? <ExpandMore /> : <ExpandLess />}
            </IconButton>
            <Tooltip title={isUploading ? "Uploads in progress, can't close" : 'Close'}>
              <span>
                <IconButton onClick={closeCard} disabled={isUploading}>
                  <Close color={isUploading ? 'disabled' : 'inherit'} />
                </IconButton>
              </span>
            </Tooltip>
          </div>
        </div>
        {isExpanded && (
          <CardContent className={classes.fileList}>
            {files.map(({ id, fileName, progress, error, retry, reset, payload }) => (
              <div key={id} className={classes.fileItem}>
                <div className={classes.fileInfo}>
                  <Typography className={classes.fileName} title={fileName}>
                    {formatFileName(fileName)}
                  </Typography>
                  <div className={classes.progressContainer}>
                    <LinearProgress
                      variant="determinate"
                      value={progress}
                      style={{ flexGrow: 1 }}
                    />
                    <Typography className={classes.progressText} variant="body2" component="span">
                      {progress}%
                    </Typography>
                  </div>
                </div>
                <div className={classes.actions}>
                  {error ? (
                    <>
                      <Tooltip
                        title={
                          <>
                            {error?.raw?.response?.status === 413 ? (
                              <>
                                <Typography variant="body1">
                                  {'Error when uploading file'}
                                </Typography>
                                <Typography variant="body2">
                                  {
                                    'Verify that the correct endpoint is used and that the image library location has sufficient space.'
                                  }
                                </Typography>
                              </>
                            ) : (
                              <>
                                <Typography variant="body1">
                                  {error?.message || 'Something went wrong. Please retry'}
                                </Typography>
                                <Typography variant="body2">
                                  {error?.message === 'Network Error'
                                    ? 'Problem connecting with the Image library service. Make sure that the Image Library server is accessible from the network you are currently on.'
                                    : error?.raw?.stack}
                                </Typography>
                              </>
                            )}
                          </>
                        }
                      >
                        <Report color="error" fontSize="small" />
                      </Tooltip>
                      <Tooltip title="Retry">
                        <IconButton
                          disabled={creating || deleting || updating}
                          size="small"
                          onClick={() => handleRetry(id, payload, retry)}
                        >
                          {creating || deleting || updating ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            <Replay color="error" fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </>
                  ) : progress < 100 ? (
                    <Tooltip title="Cancel">
                      <IconButton size="small" onClick={() => handleCancel(id, reset, payload)}>
                        <Cancel fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <CheckCircle
                      className={classes.successIcon}
                      onClick={() => resetFileStatus(id)}
                    />
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        )}
      </Card>
      {showConfirmationDialog && (
        <ConfirmationDialog
          open
          title="Discard Image?"
          text="Are you sure you want to discard the image?"
          onConfirm={handleConfirm}
          onCancel={() => setShowConfirmationDialog(false)}
          loading={deleting}
          error={deleteImageError}
        />
      )}
    </>
  ) : null
}

export default FileUploadProgress
